import React from "react"
import { useTranslation } from "next-i18next"
import { twMerge } from "tailwind-merge"
import { GameStatusSvg } from "../../assets/svg"
import { GameStatusEnum } from "../../services/games"
import CountdownTimer from "../CountdownTimer"
import { ProgressBar } from "./ProgressBar"
import { getStatusColor, getTitle1, getTitle2 } from "../../utils/string"
import { GameHashtags } from "./GameHashtags"
import { MyGameAction } from "./MyGameAction"
import refundImg from "../../assets/imgs/my-games/refund.png"
import winImg from "../../assets/imgs/my-games/win.png"
import { isAfterToday, isBeforeToday } from "../../utils/date"
import { ProgressBar2Line } from "./ProgressBar2Line"
import RetainQueryLink from "../shared/RetainQueryLink"
import { GameBadge } from "../shared/GameBadge"

interface GameItemProps {
    game: any
    hasWindow: boolean
    className?: string
    isShowUserData?: boolean
}

export const GameItem = ({ game, hasWindow, className, isShowUserData }: GameItemProps) => {
    const { t: tCommon } = useTranslation("common")
    const { t: tMyGame } = useTranslation("my-game")
    const {
        id,
        slug,
        expired,
        resultAnnouncementAt,
        resultAnnouncementExpiredAt,
        title,
        status,
        statusText,
        statusCode,
        hashtags,
        result,
        type: gameType
    } = game

    const isRefund = status === GameStatusEnum.CREATE_REJECTED
    const isEnd = [GameStatusEnum.RESULT_APPROVED].indexOf(status) >= 0

    const isBetting = status === GameStatusEnum.CREATE_APPROVED && isAfterToday(expired)
    const isEndBetting = !isBetting && isBeforeToday(expired) && !isRefund && !isEnd

    const startTime = new Date().getTime() / 1000
    const endTime = isBetting ? expired : resultAnnouncementExpiredAt

    const statusColor = getStatusColor(statusCode)

    return (
        <>
            <div
                className={twMerge(
                    "group relative flex w-full flex-col justify-between rounded-[11px] border-[0.75px] border-[#CCCCCC] bg-[#F3F3F3] transition duration-500 lg:mx-0 lg:min-h-[160px] lg:w-full lg:flex-row lg:pb-[4px] lg:hover:translate-x-[-10px] lg:hover:border-[#851A92] lg:hover:bg-[#F8F8F8]",
                    className
                )}
            >
                <div className={"flex-1 pb-[30px] lg:pb-0"}>
                    <div className={"mt-[9px] flex items-center justify-between mr-5 lg:mr-[43px]"}>
                        <div className={"flex items-center"}>
                            <div className={twMerge("relative h-[25px] w-[122px] text-12 font-medium")}>
                                <div className={"absolute left-[-2px]"}>
                                    <GameStatusSvg width={122} height={25} className={statusColor.icon} />
                                </div>
                                <div className={twMerge("relative ml-[19px] leading-[25px]", statusColor.text)}>
                                    {statusText}
                                </div>
                            </div>
                            <GameHashtags hashtags={hashtags} className={"h-[20px]"} />
                        </div>
                        <GameBadge gameType={gameType} isGameDetails={false} />
                    </div>
                    <div className={"ellips-2lines mt-[9px] ml-[19px]"}>
                        <RetainQueryLink href={`/game-details/${slug}-${id}`}>
                            <a
                                className={"text-base font-bold leading-[19px] text-neutral-1-600 hover:text-accent-1"}
                                title={title}
                            >
                                {title}
                            </a>
                        </RetainQueryLink>
                    </div>
                    <div className={"hidden lg:block"}>
                        <ProgressBar game={game} isShowUserData={isShowUserData} />
                    </div>
                    <div className={"lg:hidden"}>
                        <ProgressBar2Line game={game} isShowUserData={isShowUserData} />
                    </div>
                </div>
                <div
                    className={
                        "b-flex-center relative ml-[-1px] mt-[-1px] h-[28px] w-[100.5%] overflow-hidden lg:ml-0 lg:h-[164px] lg:w-[28px]"
                    }
                >
                    <div
                        className={
                            "absolute top-0 left-0 h-[28px] w-[28px] translate-x-[-14px] rounded-full border-[0.75px] border-[#CCCCCC] bg-white group-hover:border-[#851A92] lg:left-auto lg:translate-y-[-14px] lg:translate-x-0"
                        }
                    ></div>
                    <div
                        className={
                            "lg:width-[1px] h-[1px] w-full border-t-[0.75px] border-dashed border-[#CECECE] lg:h-full lg:w-auto lg:border-l-[0.75px] lg:border-t-0"
                        }
                    ></div>
                    <div
                        className={
                            "absolute bottom-0 right-0 h-[28px] w-[28px] translate-x-[14px] rounded-full border-[0.75px] border-[#CCCCCC] bg-white group-hover:border-[#851A92] lg:right-auto lg:translate-y-[14px] lg:translate-x-0"
                        }
                    ></div>
                </div>
                <div className={"b-flex-center flex-col py-5 pr-[14px] lg:w-[308px] lg:py-0"}>
                    {isBetting && (
                        <div className={"mb-6 font-bold leading-[19px] text-[#921A1A]"}>{tCommon("stopBetting")}</div>
                    )}
                    {isEndBetting && (
                        <div className={"mb-6 font-bold leading-[19px] text-[#FEA542]"}>
                            {tCommon("announceResult")}
                        </div>
                    )}
                    {(isBetting || isEndBetting) && hasWindow && (
                        <CountdownTimer startTime={startTime} endTime={endTime} />
                    )}
                    {isShowUserData && <MyGameAction game={game} />}
                    {!isShowUserData && (
                        <>
                            {isRefund && (
                                <div className={"b-flex-center mr-[14px] lg:flex-col"}>
                                    <img src={refundImg.src} className={"mr-4 lg:mr-0"} />
                                    <div className={"mt-[2px] text-20 font-bold uppercase leading-6"}>
                                        {tMyGame("refund")}
                                    </div>
                                </div>
                            )}
                            {isEnd && (
                                <div className={"b-flex-center mr-[14px] lg:flex-col"}>
                                    <img src={winImg.src} className={"mr-4 lg:mr-0"} />
                                    <div className={"mt-[2px] text-24 font-bold leading-6 text-[#FF0000]"}>
                                        {tCommon(result === 1 ? getTitle1(gameType) : getTitle2(gameType))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    )
}
